import moment from 'moment-timezone';
import { useEffect, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';

const getDateMoment = (startStr, endStr, timezone) => {
  if (
    !startStr ||
    !endStr ||
    typeof startStr !== 'string' ||
    typeof endStr !== 'string'
  ) {
    return null;
  }

  const timeZone = timezone || moment.tz.guess();

  // Parse dates with timezone
  const startMoment = moment.tz(startStr, timeZone);
  const endMoment = moment.tz(endStr, timeZone);

  // Validate parsed dates
  if (!startMoment.isValid() || !endMoment.isValid()) {
    return null;
  }

  // Calculate duration
  const durationInMinutes = Math.round(
    (endMoment.valueOf() - startMoment.valueOf()) / (1000 * 60)
  );

  // Validate duration
  if (durationInMinutes <= 0 || durationInMinutes > 1440) {
    return null;
  }

  return { startMoment, endMoment, durationInMinutes };
};

export const useSchEventHandlers = ({
  bookedSlot,
  timezone,
  updateSharedData,
  calendarRef,
}) => {
  const SLOT_DURATION = 15; // Matches slotDuration="00:15:00" in FullCalendar
  const DRAG_THRESHOLD = 5; // Pixels to consider a drag
  const MAX_DURATION_MINUTES = 24 * 60; // 24 hours in minutes
  const mouseDownPos = useRef(null);

  useEffect(() => {
    const calendarEl = calendarRef.current?.getApi().el;

    if (!calendarEl) {
      return; // Silently exit if calendar element isn’t found
    }

    const handleMouseDown = (event) => {
      mouseDownPos.current = { x: event?.clientX, y: event?.clientY };
    };

    calendarEl.addEventListener('mousedown', handleMouseDown);

    return () => {
      calendarEl.removeEventListener('mousedown', handleMouseDown);
    };
  }, [calendarRef]);

  const handleSelect = (info) => {
    // Extract previous duration from bookedSlot, defaulting to SLOT_DURATION if unset
    const { duration: prevDuration = SLOT_DURATION } = bookedSlot ?? {};
    const { startStr, endStr, allDay = false, jsEvent } = info || {};

    // Calculate the duration of the current selection in minutes
    const currentDuration = moment(endStr).diff(moment(startStr), 'minutes');

    // Determine if the selection was a drag by comparing mouse down and up positions
    const mouseUpPos = { x: jsEvent?.clientX, y: jsEvent?.clientY };
    const dx = mouseDownPos.current
      ? Math.abs(mouseUpPos.x - mouseDownPos.current.x)
      : 0;
    const dy = mouseDownPos.current
      ? Math.abs(mouseUpPos.y - mouseDownPos.current.y)
      : 0;
    const wasDragged = dx > DRAG_THRESHOLD || dy > DRAG_THRESHOLD;
    // Business Logic: A click is no drag motion and duration up to one slot (15 minutes)
    const isClick = !wasDragged && currentDuration <= SLOT_DURATION;

    // Business Logic: Determine new duration
    // - If previous duration exceeded 24 hours, reset clicks to 15 minutes
    // - Use currentDuration for drags, capped at 24 hours
    let newDuration = isClick ? prevDuration : currentDuration;

    if (newDuration > MAX_DURATION_MINUTES) {
      newDuration = MAX_DURATION_MINUTES; // Cap at 24 hours
    }
    const newEnd = isClick
      ? moment.tz(startStr, timezone).add(prevDuration, 'minutes').toISOString()
      : endStr;

    // Update the shared data with the new booked slot details
    updateSharedData('bookedSlot', {
      ...bookedSlot,
      duration: newDuration,
      start: startStr,
      end: newEnd,
      allDay,
      id: uuidv4(),
      extendedProps: {
        ...bookedSlot?.extendedProps,
        isOriginalSlotModified: true,
      },
    });

    // Reset mouse position for the next interaction
    mouseDownPos.current = null;
  };

  const handleEventResize = (info) => {
    const { event } = info;
    const { startStr, endStr } = event;
    const validation = getDateMoment(startStr, endStr, timezone);
    if (!validation) return info.revert();

    const { durationInMinutes } = validation;
    updateSharedData({
      'bookedSlot.duration': durationInMinutes,
      'bookedSlot.start': startStr,
      'bookedSlot.end': endStr,
      'bookedSlot.extendedProps': {
        ...bookedSlot?.extendedProps,
        isOriginalSlotModified: true,
      },
    });
  };

  const handleEventDrop = (info) => {
    const { event } = info;
    const { startStr, endStr } = event;
    const validation = getDateMoment(startStr, endStr, timezone);
    if (!validation) return info.revert();

    const { durationInMinutes } = validation;
    updateSharedData({
      'bookedSlot.duration': durationInMinutes,
      'bookedSlot.start': startStr,
      'bookedSlot.end': endStr,
      'bookedSlot.extendedProps': {
        ...bookedSlot?.extendedProps,
        isOriginalSlotModified: true,
      },
    });
  };

  return {
    handleSelect,
    handleEventResize,
    handleEventDrop,
  };
};
